import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Clock, CheckCircle2, XCircle } from "lucide-react";

const STATUS_META = {
  pending: { label: "İnceleme Bekliyor", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  approved: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  rejected: { label: "Reddedildi", cls: "bg-rose-100 text-rose-800", Icon: XCircle },
};

export default function TeacherApplication() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", description: "", kvkk_consent: false });

  const load = async () => {
    const { data } = await api.get("/teacher/my-application");
    setState(data);
    setShowForm(!data.application);
  };

  useEffect(() => {
    setForm((f) => ({ ...f, full_name: user?.name || "" }));
    load().catch(() => setShowForm(true));
    // eslint-disable-next-line
  }, []);

  const app = state?.application;
  const meta = app ? STATUS_META[app.status] : null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/teacher/apply", form);
      toast.success("Başvurun alındı. İnceleme sonrası bilgilendirileceksin.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Başvuru gönderilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="teacher-apply-page">
      <h1 className="font-display text-3xl tracking-tight text-[#0B1A35]">Öğretmen Başvurusu</h1>

      {/* Durum kartı */}
      {app && !showForm && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3"
             data-testid="teacher-application-status">
          <div className="flex items-center justify-between">
            <div className="font-display text-lg">Başvuru Durumun</div>
            <span data-testid="teacher-application-status-badge"
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${meta.cls}`}>
              <meta.Icon size={13} /> {meta.label}
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Başvuru tarihi: {new Date(app.created_at).toLocaleString("tr-TR")}
          </p>
          {app.status === "pending" && (
            <p className="text-sm text-slate-500">Başvurun alındı ve yönetici incelemesini bekliyor.</p>
          )}
          {app.status === "approved" && (
            <Link to="/ogretmen" data-testid="teacher-panel-link"
                  className="yz-cta inline-block px-4 py-2 rounded-lg text-sm">
              Öğretmen Paneline Git
            </Link>
          )}
          {app.status === "rejected" && (
            <div className="space-y-2">
              {app.review_note && (
                <p className="text-sm text-slate-600">Not: {app.review_note}</p>
              )}
              <button onClick={() => setShowForm(true)}
                      data-testid="teacher-reapply-btn"
                      className="yz-secondary px-4 py-2 rounded-lg text-sm">
                Yeniden Başvur
              </button>
            </div>
          )}
        </div>
      )}

      {/* Başvuru formu */}
      {showForm && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4"
             data-testid="teacher-apply-form">
          <div className="flex items-center gap-2">
            <GraduationCap size={18} className="text-[#007BFF]" />
            <div className="font-display text-lg">Öğretmen Paneline Geç</div>
          </div>

          <div>
            <label className="text-xs text-slate-500">E-posta (oturumundaki hesap)</label>
            <div className="mt-1 px-3 py-2 rounded-lg bg-slate-50 border border-[#E2E8F0] text-sm text-slate-600"
                 data-testid="teacher-apply-email">
              {user?.email}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500">Ad Soyad *</label>
            <input value={form.full_name}
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                   data-testid="teacher-apply-fullname"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#007BFF]" />
          </div>

          <div>
            <label className="text-xs text-slate-500">Telefon Numarası *</label>
            <input value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="05xx xxx xx xx"
                   data-testid="teacher-apply-phone"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#007BFF]" />
            <p className="mt-1 text-xs text-slate-500">
              Telefon numaran yalnızca başvurunun doğrulanması ve gerektiğinde seninle iletişime
              geçilmesi için istenir; hiçbir öğrenci ekranında veya herkese açık profilde gösterilmez.
            </p>
          </div>

          <div>
            <label className="text-xs text-slate-500">Kısa Açıklama *</label>
            <textarea value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={4}
                      placeholder="Öğretmenlik durumun ve YZ Matematik'i nasıl kullanmak istediğin"
                      data-testid="teacher-apply-description"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#007BFF]" />
          </div>

          {/* KVKK aydınlatma */}
          <div className="rounded-lg bg-slate-50 border border-[#E2E8F0] p-3 text-xs text-slate-600 space-y-1"
               data-testid="teacher-apply-kvkk-text">
            <div className="font-medium text-slate-700">Kişisel Verilerin Korunması Aydınlatma Metni</div>
            <p>
              Bu formda paylaştığın ad soyad, e-posta ve telefon bilgileri yalnızca öğretmen
              başvurunun değerlendirilmesi amacıyla işlenir; yalnızca site yöneticisi tarafından
              görüntülenir, üçüncü kişilerle paylaşılmaz ve öğrenci ekranlarında gösterilmez.
              Başvurunun reddedilmesi hâlinde kayıt sistemde saklanmaya devam eder; silinmesini
              talep edebilirsin.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox"
                   checked={form.kvkk_consent}
                   onChange={(e) => setForm({ ...form, kvkk_consent: e.target.checked })}
                   data-testid="teacher-apply-kvkk-checkbox"
                   className="mt-0.5" />
            <span>Aydınlatma metnini okudum; kişisel verilerimin başvuru değerlendirmesi amacıyla işlenmesini onaylıyorum. *</span>
          </label>

          <div className="flex gap-2">
            <button onClick={submit}
                    disabled={busy || !form.full_name.trim() || !form.phone.trim() ||
                              form.description.trim().length < 20 || !form.kvkk_consent}
                    data-testid="teacher-apply-submit"
                    className="yz-cta px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              {busy ? "Gönderiliyor…" : "Başvuruyu Gönder"}
            </button>
            {app && (
              <button onClick={() => setShowForm(false)}
                      data-testid="teacher-apply-cancel"
                      className="px-4 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                Vazgeç
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
